import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CategoryTypeOfSearch, ResultOfMainSearch } from '@core/interfaces/main-search';
import { HubDestroyService } from '@core/services/hub-destroy/hub-destroy.service';
import { MainSearchStoreService } from '@core/services/main-search/main-search-store.service';
import { MainSearchService, RecentSearch } from '@core/services/main-search/main-search.service';
import { fromEvent, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CloseOnOutsideClickModule } from '@shared/directives/close-on-outside-click/close-on-outside-click.module';
import { CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { HubIconComponent } from '@shared/components/hub-icon/hub-icon.component';

@Component({
  selector: 'hub-main-header-searchbar',
  templateUrl: './main-header-searchbar.component.html',
  styleUrls: ['./main-header-searchbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HubDestroyService],
  imports: [
    CommonModule,
    NgbTooltipModule,
    ReactiveFormsModule,
    HubIconComponent,
    RouterModule,
    CloseOnOutsideClickModule,
  ],
})
export class MainHeaderSearchbarComponent implements AfterViewInit {
  private searchService = inject(MainSearchService);
  searchStore = inject(MainSearchStoreService);
  private router = inject(Router);
  private hubDestroyService = inject(HubDestroyService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly searchInputRef = viewChild.required<ElementRef>('searchInput');

  isVisibleSearchPreview: boolean;

  activeCategoryType: CategoryTypeOfSearch;

  recentHistory: RecentSearch[] = this.searchService.getRecentSearches();

  form: UntypedFormGroup;

  categoryTypes = CategoryTypeOfSearch;

  isVisibleEmptyResultsState: boolean;

  results: ResultOfMainSearch[];

  projectsResults$: Observable<ResultOfMainSearch[]>;

  modulesResults$: Observable<ResultOfMainSearch[]>;

  assetsResults$: Observable<ResultOfMainSearch[]>;

  contractsResults$: Observable<ResultOfMainSearch[]>;

  methodsResults$: Observable<ResultOfMainSearch[]>;

  constructor() {
    this.results = [];
    this.activeCategoryType = CategoryTypeOfSearch.Any;
    this.isVisibleEmptyResultsState = false;
    this.isVisibleSearchPreview = false;

    this.projectsResults$ = this.searchStore.projectsResults$.pipe(map((arr) => arr.slice(0, 5)));
    this.modulesResults$ = this.searchStore.modulesResults$.pipe(map((arr) => arr.slice(0, 5)));
    this.assetsResults$ = this.searchStore.assetsResults$.pipe(map((arr) => arr.slice(0, 5)));
    this.contractsResults$ = this.searchStore.contractsResults$.pipe(map((arr) => arr.slice(0, 5)));
    this.methodsResults$ = this.searchStore.methodsResults$.pipe(map((arr) => arr.slice(0, 5)));

    this.form = new UntypedFormGroup({
      text: new UntypedFormControl('', [Validators.minLength(3), Validators.required]),
    });
  }

  ngAfterViewInit(): void {
    this.listenInput();
  }

  resetRecentHistory(event?: Event): void {
    this.searchService.clearRecentSearch();
    this.recentHistory = this.searchService.getRecentSearches();
    event?.stopPropagation();
    this.changeDetectorRef.detectChanges();
  }

  onChangeFocusState(state: boolean): void {
    this.isVisibleSearchPreview = state;
    if (!state) {
      this.form.reset();
      this.searchStore.setState([]);
    }
    this.changeDetectorRef.detectChanges();
  }

  selectCategory(cat: CategoryTypeOfSearch): void {
    this.activeCategoryType = cat;
    this.preSearch();
  }

  onResetSearchField(event: Event): void {
    event.stopPropagation();
    this.form.reset();
    this.searchStore.setState([]);
    this.changeDetectorRef.detectChanges();
  }

  searchRecent(recentSearch: RecentSearch): void {
    this.isVisibleSearchPreview = false;
    this.router.navigate(['/search'], {
      queryParams: recentSearch,
    });
  }

  submit(): void {
    if (this.form.valid) {
      this.isVisibleEmptyResultsState = false;
      this.searchInputRef()?.nativeElement.select();
      this.redirectToResultsPage();
    }
  }

  navigateToEntityPage(
    baseUrl: string,
    entityId: string,
    entityType: string,
    parentId: string,
    subParentId: string,
  ): string[] {
    switch (entityType) {
      case CategoryTypeOfSearch.Asset: {
        return [baseUrl, parentId, 'assets', entityId];
      }
      case CategoryTypeOfSearch.Contract: {
        return [baseUrl, parentId, 'contract'];
      }
      case CategoryTypeOfSearch.Method: {
        return [baseUrl, parentId, 'contract', subParentId, 'method', entityId];
      }
      default: {
        return [baseUrl, entityId];
      }
    }
  }

  private preSearch(): void {
    if (this.form.valid) {
      const { text } = this.form.value;
      const data = {
        offset: 0,
        text,
        type:
          this.activeCategoryType !== CategoryTypeOfSearch.Bookmark
            ? this.activeCategoryType
            : CategoryTypeOfSearch.Any,
        bookmarked: this.activeCategoryType === CategoryTypeOfSearch.Bookmark ? true : undefined,
      };

      this.searchService
        .search(data)
        .pipe(this.hubDestroyService.takeUntilDestroy())
        .subscribe((res) => {
          this.searchStore.setState(res.elements);
          this.isVisibleEmptyResultsState = !res.elements.length;
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  private listenInput(): void {
    fromEvent<KeyboardEvent>(this.searchInputRef().nativeElement, 'keyup')
      .pipe(this.hubDestroyService.takeUntilDestroy())
      .subscribe((event: KeyboardEvent) => {
        if (event.key !== 'Enter') {
          this.isVisibleSearchPreview = true;
          if (this.form.valid) {
            this.preSearch();
          } else {
            this.searchStore.setState([]);
            this.isVisibleEmptyResultsState = false;
          }
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private redirectToResultsPage(): void {
    this.isVisibleSearchPreview = false;
    const searchInputRef = this.searchInputRef();
    const params = {
      category: this.activeCategoryType,
      text: searchInputRef?.nativeElement?.value,
      bookmarked: this.activeCategoryType === CategoryTypeOfSearch.Bookmark,
    };
    this.searchService.addRecentSearch(params);
    this.router.navigate(['/search'], {
      queryParams: params,
    });
    this.form.reset();
    searchInputRef?.nativeElement.blur();
    this.searchStore.setState([]);
  }
}
